// WAV parser: RIFF (little-endian), RIFX (big-endian), RF64 and BW64 "WAVE" files.
//
// Layout choice: the 12-byte RIFF header is its own 'header' segment and every
// chunk is a top-level segment. Formally the RIFF chunk is a container that holds
// all other chunks, but the app colours top-level segments by kind. With the
// chunks at the top level, the byte map shows fmt (header), LIST (metadata),
// JUNK (padding) and data (audio) in distinct colours instead of one big
// "container" block.
//
// This file also exports small PCM helpers (sample decoding, per-second blocks)
// that the AIFF parser shares.

import { ascii, channelName, field, fmtBytes, fmtHz, fmtKbps, hex, matches, plural, u16be, u16le, u32be, u32le, u64be, u64le } from './bits';
import { parseId3v1, parseId3v2 } from './id3';
import type { AudioInfo, Explanation, Field, FormatParser, Note, ParsedFile, PcmLayout, Picture, Segment, Tag } from './types';

// ─── Shared helpers (also used by aiff.ts) ──────────────────────────────────

const MINUS = '−';
/** "−1,234" with a real minus sign. */
export const signedNum = (n: number) => (n < 0 ? MINUS : '') + Math.abs(n).toLocaleString('en-US');
const frac = (x: number, digits = 4) => (x < 0 ? MINUS : '') + Math.abs(x).toFixed(digits);

/** Clock time for block names: "0:07", "0:07.63", "1:02:03". */
export function fmtClock(t: number): string {
  const whole = Math.abs(t - Math.round(t)) < 1e-9;
  const s = whole ? Math.round(t) : t;
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s - h * 3600 - m * 60;
  const sec = whole ? String(Math.round(r)).padStart(2, '0') : r.toFixed(2).padStart(5, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

/** True when 4 bytes at `o` are printable ASCII, as every chunk ID must be. */
export function validId(b: Uint8Array, o: number): boolean {
  if (o < 0 || o + 4 > b.length) return false;
  for (let i = 0; i < 4; i++) if (b[o + i] < 0x20 || b[o + i] > 0x7e) return false;
  return true;
}

/** Drops fields that fall outside [start, end). This protects us from truncated files. */
export function fitFields(fields: Field[], start: number, end: number): Field[] {
  return fields.filter((f) => f.bits > 0 && f.bit >= start * 8 && f.bit + f.bits <= end * 8);
}

const fatalUtf8 = new TextDecoder('utf-8', { fatal: true });
/** Text from a chunk: UTF-8 when valid, else Latin-1. Trailing NULs and spaces are removed. */
export function chunkText(b: Uint8Array, start: number, len: number): string {
  const sub = b.subarray(start, Math.min(b.length, start + Math.max(0, len)));
  let s: string;
  try {
    s = fatalUtf8.decode(sub);
  } catch {
    s = ascii(b, start, len);
  }
  const nul = s.indexOf('\0');
  return (nul >= 0 ? s.slice(0, nul) : s).trimEnd();
}
const quote = (s: string, max = 200) => (s ? `"${s.length > max ? s.slice(0, max) + '…' : s}"` : '(empty)');

export type SampleCoding = 'uint8' | 'int' | 'float' | 'alaw' | 'ulaw';
/** How to read one sample of uncompressed (or G.711) audio. */
export interface SampleFormat {
  coding: SampleCoding;
  /** Bytes per sample (the container size, e.g. 3 for packed 24-bit). */
  bytes: number;
  bigEndian: boolean;
  channels: number;
  /** Bytes per sample frame (all channels). */
  blockAlign: number;
  /** Short channel labels, e.g. ["L", "R"]. */
  labels: string[];
}

function readInt(b: Uint8Array, o: number, n: number, be: boolean): number {
  let v = 0;
  for (let i = 0; i < n; i++) v = v * 256 + b[be ? o + i : o + n - 1 - i];
  const full = 2 ** (8 * n);
  return v >= full / 2 ? v - full : v;
}
/** G.711 µ-law code → 16-bit linear value. */
function ulawToLinear(u: number): number {
  u = ~u & 0xff;
  const t = (((u & 0x0f) << 3) + 0x84) << ((u >> 4) & 7);
  return u & 0x80 ? 0x84 - t : t - 0x84;
}
/** G.711 A-law code → 16-bit linear value. */
function alawToLinear(a: number): number {
  a ^= 0x55;
  const seg = (a >> 4) & 7;
  let t = (a & 0x0f) << 4;
  t += seg === 0 ? 8 : 0x108;
  if (seg > 1) t <<= seg - 1;
  return a & 0x80 ? t : -t;
}

/** Decodes one sample to a display string and a raw number. */
function sampleValue(b: Uint8Array, o: number, f: SampleFormat): { text: string; raw: number } {
  switch (f.coding) {
    case 'uint8': {
      const v = b[o], s = v - 128;
      return { text: `${signedNum(s)} (${frac(s / 128)}) · stored as ${v}`, raw: v };
    }
    case 'int': {
      const v = readInt(b, o, f.bytes, f.bigEndian);
      return { text: `${signedNum(v)} (${frac(v / 2 ** (8 * f.bytes - 1))})`, raw: v };
    }
    case 'float': {
      const dv = new DataView(b.buffer, b.byteOffset + o, f.bytes);
      const x = f.bytes === 8 ? dv.getFloat64(0, !f.bigEndian) : dv.getFloat32(0, !f.bigEndian);
      return { text: Number.isFinite(x) && Math.abs(x) < 1000 ? frac(x, 6) : String(x), raw: x };
    }
    case 'alaw':
    case 'ulaw': {
      const v = f.coding === 'alaw' ? alawToLinear(b[o]) : ulawToLinear(b[o]);
      return { text: `${signedNum(v)} (${frac(v / 32768)}) · code ${hex(b[o])}`, raw: b[o] };
    }
  }
}

const SAMPLE_NOTES: Record<SampleCoding, string> = {
  uint8: '8-bit samples are unsigned: 128 is silence, 0 and 255 are the extremes.',
  int: 'A signed whole number. The value in brackets is its share of full scale (−1 to 1).',
  float: 'A floating-point sample. ±1.0 is full scale; values past that can clip on playback.',
  alaw: 'An 8-bit A-law code, expanded here to its 16-bit linear value.',
  ulaw: 'An 8-bit µ-law code, expanded here to its 16-bit linear value.',
};

/**
 * Decodes `count` sample frames, starting at frame `fromFrame` (relative to
 * `dataStart`), into one field per channel: "Frame 0 · L" = "−1,234 (−0.0377)".
 */
export function sampleFields(b: Uint8Array, dataStart: number, dataEnd: number, f: SampleFormat, fromFrame: number, count: number): Field[] {
  const out: Field[] = [];
  if (f.blockAlign <= 0 || f.bytes <= 0) return out;
  for (let i = 0; i < count; i++) {
    const frame = fromFrame + i;
    const fo = dataStart + frame * f.blockAlign;
    for (let c = 0; c < f.channels; c++) {
      const o = fo + c * f.bytes;
      if (o + f.bytes > dataEnd || o + f.bytes > b.length) return out;
      const { text, raw } = sampleValue(b, o, f);
      out.push({
        name: `Frame ${frame.toLocaleString('en-US')} · ${f.labels[c] ?? `Ch ${c + 1}`}`,
        value: text, raw, bit: o * 8, bits: f.bytes * 8,
        note: i === 0 && c === 0 ? SAMPLE_NOTES[f.coding] : undefined,
      });
    }
  }
  return out;
}

const BLOCK_STEPS = [1, 2, 5, 10, 30, 60, 300, 600, 1800, 3600];

/**
 * Splits audio bytes [start, end) into blocks of whole seconds, each with its
 * time range. The block length grows for long files so there are at most ~1000.
 * `bytesPerSecond` is exact for PCM; without it the bytes are spread evenly.
 */
export function timeBlocks(
  b: Uint8Array,
  o: { start: number; end: number; duration: number; bytesPerSecond?: number; align: number; type: string; sample?: SampleFormat },
): Segment[] {
  const len = o.end - o.start;
  if (len <= 0 || !(o.duration > 0) || !Number.isFinite(o.duration)) return [];
  const bps = o.bytesPerSecond && o.bytesPerSecond > 0 ? o.bytesPerSecond : len / o.duration;
  const step = BLOCK_STEPS.find((s) => o.duration / s <= 1000) ?? Math.ceil(o.duration / 1000);
  const at = (t: number) => {
    let x = Math.round(t * bps);
    if (o.align > 1) x = Math.floor(x / o.align) * o.align;
    return o.start + Math.min(len, x);
  };
  const out: Segment[] = [];
  for (let t = 0; t < o.duration; t += step) {
    const t1 = Math.min(o.duration, t + step);
    const s = at(t), e = t1 >= o.duration ? o.end : at(t1);
    if (e <= s) continue;
    const seg: Segment = {
      kind: 'audio', type: o.type, name: `${fmtClock(t)}–${fmtClock(t1)}`, start: s, end: e, time: [t, t1],
      summary: fmtBytes(e - s),
    };
    const sf = o.sample;
    if (sf && sf.blockAlign > 0) {
      const first = Math.floor((s - o.start) / sf.blockAlign);
      seg.summary += ` · frames ${first.toLocaleString('en-US')}–${(Math.ceil((e - o.start) / sf.blockAlign) - 1).toLocaleString('en-US')}`;
      seg.fields = () => sampleFields(b, o.start, e, sf, first, 4);
    }
    out.push(seg);
  }
  return out;
}

/** A pad byte segment: RIFF and IFF chunks start on even offsets. */
export function padSegment(b: Uint8Array, at: number, type: string): Segment {
  return {
    kind: 'padding', type, name: 'Pad byte', start: at, end: at + 1,
    fields: [field('Pad byte', hex(b[at] ?? 0), at, 1, 'Keeps the next chunk on an even offset. It should be zero.', b[at])],
  };
}

// ─── WAV tables ─────────────────────────────────────────────────────────────

const FORMAT_TAGS: Record<number, [short: string, long: string]> = {
  0x0001: ['PCM', 'Linear PCM'],
  0x0002: ['ADPCM', 'Microsoft ADPCM, 4-bit'],
  0x0003: ['IEEE float', 'Linear PCM, IEEE floating point'],
  0x0006: ['A-law', 'G.711 A-law, 8-bit companded'],
  0x0007: ['µ-law', 'G.711 µ-law, 8-bit companded'],
  0x0011: ['IMA ADPCM', 'IMA / DVI ADPCM, 4-bit'],
  0x0022: ['TrueSpeech', 'DSP Group TrueSpeech'],
  0x0031: ['GSM 6.10', 'GSM 6.10 speech codec'],
  0x0040: ['G.721', 'G.721 ADPCM'],
  0x0050: ['MPEG', 'MPEG-1 Audio Layer I or II'],
  0x0055: ['MP3', 'MPEG-1 Audio Layer III'],
  0x0092: ['AC-3 (S/PDIF)', 'Dolby AC-3 over S/PDIF'],
  0x00ff: ['AAC', 'Advanced Audio Coding'],
  0x0160: ['WMA', 'Windows Media Audio v1'],
  0x0161: ['WMA', 'Windows Media Audio v2'],
  0x0162: ['WMA Pro', 'Windows Media Audio Professional'],
  0x0163: ['WMA Lossless', 'Windows Media Audio Lossless'],
  0x1610: ['AAC', 'MPEG-2/4 AAC (HEAAC)'],
  0x2000: ['AC-3', 'Dolby AC-3'],
  0x2001: ['DTS', 'DTS Coherent Acoustics'],
  0xf1ac: ['FLAC', 'FLAC'],
  0xfffe: ['Extensible', 'WAVE_FORMAT_EXTENSIBLE'],
};
const tagName = (t: number) => FORMAT_TAGS[t]?.[0] ?? `Format ${hex(t, 4)}`;

const SPEAKERS: [short: string, long: string][] = [
  ['FL', 'Front left'], ['FR', 'Front right'], ['FC', 'Front center'], ['LFE', 'Low frequency (subwoofer)'],
  ['BL', 'Back left'], ['BR', 'Back right'], ['FLC', 'Front left of center'], ['FRC', 'Front right of center'],
  ['BC', 'Back center'], ['SL', 'Side left'], ['SR', 'Side right'], ['TC', 'Top center'],
  ['TFL', 'Top front left'], ['TFC', 'Top front center'], ['TFR', 'Top front right'],
  ['TBL', 'Top back left'], ['TBC', 'Top back center'], ['TBR', 'Top back right'],
];
const LAYOUTS: Record<number, string> = {
  0x4: 'Mono', 0x3: 'Stereo', 0x7: '3.0', 0x103: '2.1', 0x33: 'Quad', 0x107: '4.0', 0x37: '5.0', 0x607: '5.0 (side)',
  0x3f: '5.1', 0x60f: '5.1 (side)', 0x13f: '6.1', 0x70f: '6.1 (side)', 0x63f: '7.1', 0xff: '7.1 (wide)',
};
/** WAV's default channel order when there is no mask (the order of mask bits). */
function defaultLabels(n: number): string[] {
  if (n === 1) return ['M'];
  if (n === 2) return ['L', 'R'];
  const order = n === 4 ? [0, 1, 4, 5] : n === 5 ? [0, 1, 2, 4, 5] : n === 8 ? [0, 1, 2, 3, 4, 5, 9, 10] : [0, 1, 2, 3, 4, 5, 8, 9, 10];
  return Array.from({ length: n }, (_, i) => (n <= 9 && order[i] !== undefined ? SPEAKERS[order[i]][0] : `Ch ${i + 1}`));
}
function maskLabels(mask: number, n: number): string[] {
  if (mask === 0x3 && n === 2) return ['L', 'R'];
  if (mask === 0x4 && n === 1) return ['M'];
  const out: string[] = [];
  for (let bit = 0; bit < 18 && out.length < n; bit++) if (mask & (1 << bit)) out.push(SPEAKERS[bit][0]);
  while (out.length < n) out.push(`Ch ${out.length + 1}`);
  return out;
}

/** Tag names for LIST/INFO items. */
const INFO_KEYS: Record<string, string> = {
  INAM: 'Title', IART: 'Artist', IPRD: 'Album', ICRD: 'Year', ICMT: 'Comment', IGNR: 'Genre', ISFT: 'Encoder',
  ITRK: 'Track', IPRT: 'Track', ICOP: 'Copyright', IENG: 'Engineer', ISBJ: 'Subject', IKEY: 'Keywords',
  ISRC: 'Source', ITCH: 'Technician', ILNG: 'Language', IMED: 'Medium', ICMS: 'Commissioned by',
  ISRF: 'Source form', IARL: 'Archival location', IMUS: 'Composer', IWRI: 'Writer', IBPM: 'BPM',
  IDIT: 'Digitized', ISMP: 'SMPTE time', ICNT: 'Country', IPRO: 'Producer', ICNM: 'Cinematographer',
  ICAS: 'Cast', ILGT: 'Lightness', IDPI: 'Dots per inch', IPLT: 'Palette', ICRP: 'Cropped', IDIM: 'Dimensions',
  ISHP: 'Sharpness', ISTR: 'Starring', IEDT: 'Edited by',
};

const PADDING_IDS = new Set(['JUNK', 'junk', 'PAD ', 'FLLR', 'padd', 'pad ', 'Fake', '    ']);
const XML_IDS = new Set(['iXML', 'axml', '_PMX']);
/** Chunks we name and explain but do not decode field by field. */
const KNOWN_OPAQUE: Record<string, [kind: Segment['kind'], type: string]> = {
  chna: ['header', 'wav.chunk.chna'],
  PEAK: ['index', 'wav.chunk.PEAK'],
  levl: ['index', 'wav.chunk.levl'],
  DISP: ['metadata', 'wav.chunk.DISP'],
  umid: ['metadata', 'wav.chunk.umid'],
  minf: ['metadata', 'wav.chunk.pro'],
  elm1: ['metadata', 'wav.chunk.pro'],
  regn: ['metadata', 'wav.chunk.pro'],
  ResU: ['metadata', 'wav.chunk.pro'],
  SyLp: ['metadata', 'wav.chunk.pro'],
  plst: ['index', 'wav.chunk.plst'],
};

// ─── WAV parsing ────────────────────────────────────────────────────────────

interface Reader {
  be: boolean;
  u16: (b: Uint8Array, o: number) => number;
  u32: (b: Uint8Array, o: number) => number;
  u64: (b: Uint8Array, o: number) => number;
  i16: (b: Uint8Array, o: number) => number;
}
const reader = (be: boolean): Reader => ({
  be,
  u16: be ? u16be : u16le,
  u32: be ? u32be : u32le,
  u64: be ? u64be : u64le,
  i16: be ? (b, o) => (u16be(b, o) << 16) >> 16 : (b, o) => (u16le(b, o) << 16) >> 16,
});

interface RawChunk {
  id: string;
  start: number;
  /** Size as written in the header. */
  declared: number;
  bodyStart: number;
  bodyEnd: number;
  /** End including the pad byte. */
  end: number;
  pad: boolean;
  /** Where the real size came from when it is not the header's own. */
  sizeSource?: string;
  warning?: string;
}

interface Fmt {
  tag: number;
  channels: number;
  sampleRate: number;
  byteRate: number;
  blockAlign: number;
  bits: number;
  validBits?: number;
  channelMask?: number;
  subTag?: number;
  samplesPerBlock?: number;
  /** Tag after resolving EXTENSIBLE → SubFormat. */
  eff: number;
  fields: Field[];
}

interface Ctx {
  b: Uint8Array;
  r: Reader;
  size: number;
  fmt?: Fmt;
  factSamples?: number;
  ds64?: { riffSize: number; dataSize: number; sampleCount: number; table: Map<string, number> };
  tags: Tag[];
  pictures: Picture[];
  notes: Note[];
  labels: Map<number, string>;
  encoder?: string;
}

function parseDs64(ctx: Ctx, at: number): Ctx['ds64'] {
  const { b } = ctx;
  if (!matches(b, at, 'ds64') || at + 36 > b.length) return undefined;
  const size = u32le(b, at + 4);
  const p = at + 8;
  const table = new Map<string, number>();
  const n = u32le(b, p + 24);
  for (let i = 0, q = p + 28; i < n && q + 12 <= Math.min(b.length, p + size); i++, q += 12) table.set(ascii(b, q, 4), u64le(b, q + 4));
  return { riffSize: u64le(b, p), dataSize: u64le(b, p + 8), sampleCount: u64le(b, p + 16), table };
}

/** Pass 1: finds the chunk boundaries without decoding them. */
function walkChunks(ctx: Ctx, riffEnd: number, rf64: boolean): { chunks: RawChunk[]; tail: number } {
  const { b, r, size } = ctx;
  const chunks: RawChunk[] = [];
  let pos = 12;
  while (pos + 8 <= size) {
    if (!validId(b, pos)) break;
    const id = ascii(b, pos, 4);
    // Past the declared RIFF end, accept only chunks that fit in the file.
    const declared = r.u32(b, pos + 4);
    let len = declared;
    let sizeSource: string | undefined;
    let warning: string | undefined;
    if (rf64 && declared === 0xffffffff && ctx.ds64) {
      const fromTable = id === 'data' ? ctx.ds64.dataSize : ctx.ds64.table.get(id);
      if (fromTable !== undefined) {
        len = fromTable;
        sizeSource = 'ds64';
      }
    }
    const bodyStart = pos + 8;
    if (pos >= riffEnd && bodyStart + len > size) break;
    if (id === 'data') {
      // Streamed recordings write 0 or 0xFFFFFFFF and never come back to fix it.
      const zeroButAudio = declared === 0 && bodyStart < size && !(validId(b, bodyStart) && bodyStart + 8 + r.u32(b, bodyStart + 4) <= size);
      if (zeroButAudio || (declared === 0xffffffff && !sizeSource)) {
        len = size - bodyStart;
        sizeSource = 'end of file';
        warning = `The size field says ${declared === 0 ? '0' : '0xFFFFFFFF'}, which means the writer never filled it in (a streamed or interrupted recording). The audio is assumed to run to the end of the file.`;
      }
    }
    if (bodyStart + len > size) {
      warning = `The chunk says it holds ${len.toLocaleString('en-US')} bytes, but the file ends after ${(size - bodyStart).toLocaleString('en-US')}. The file is probably truncated.`;
      len = size - bodyStart;
      sizeSource = 'end of file';
    }
    const bodyEnd = bodyStart + len;
    let pad = len % 2 === 1 && bodyEnd < size;
    // Some writers forget the pad byte. If the next chunk ID starts right at the body end, trust that.
    if (pad && bodyEnd + 1 + 8 <= size && !validId(b, bodyEnd + 1) && validId(b, bodyEnd)) {
      pad = false;
      warning = 'This chunk has an odd size but no pad byte after it. The format requires one; some programs forget it.';
    }
    if (pos >= riffEnd && !warning) warning = 'This chunk lies after the end of the RIFF chunk, as given by the RIFF size. Some programs append data like this.';
    chunks.push({ id, start: pos, declared, bodyStart, bodyEnd, end: bodyEnd + (pad ? 1 : 0), pad, sizeSource, warning });
    pos = bodyEnd + (pad ? 1 : 0);
  }
  return { chunks, tail: pos };
}

function parseFmt(ctx: Ctx, c: RawChunk): Fmt {
  const { b, r } = ctx;
  const p = c.bodyStart, len = c.bodyEnd - c.bodyStart;
  const tag = r.u16(b, p), channels = r.u16(b, p + 2), sampleRate = r.u32(b, p + 4);
  const byteRate = r.u32(b, p + 8), blockAlign = r.u16(b, p + 12), bits = len >= 16 ? r.u16(b, p + 14) : 0;
  const fields: Field[] = [
    field('Format tag', `${tagName(tag)} (${hex(tag, 4)})`, p, 2, 'Says how the samples are coded. 1 means plain PCM, 3 means floating point.', tag),
    field('Channels', `${channels} (${channelName(channels)})`, p + 2, 2, 'Number of channels. Their samples are interleaved in each sample frame.', channels),
    field('Sample rate', `${sampleRate.toLocaleString('en-US')} Hz`, p + 4, 4, 'Sample frames per second.', sampleRate),
    field('Byte rate', `${byteRate.toLocaleString('en-US')} bytes/s`, p + 8, 4, 'Bytes of audio per second. Players use it to turn a byte position into a time.', byteRate),
    field('Block align', `${blockAlign} bytes`, p + 12, 2, 'Size of one sample frame (one sample for every channel). Players always read whole blocks.', blockAlign),
    field('Bits per sample', `${bits}`, p + 14, 2, 'Resolution of each sample. More bits give a lower noise floor.', bits),
  ];
  const f: Fmt = { tag, channels, sampleRate, byteRate, blockAlign, bits, eff: tag, fields };
  if (len < 18) return f;
  const cb = r.u16(b, p + 16);
  fields.push(field('Extra size', `${cb} bytes`, p + 16, 2, 'How many format-specific bytes follow. Plain PCM needs none.', cb));
  const x = p + 18, xEnd = Math.min(c.bodyEnd, x + cb);
  if (tag === 0xfffe && xEnd - x >= 22) {
    const valid = r.u16(b, x), mask = r.u32(b, x + 2);
    f.validBits = valid;
    f.channelMask = mask;
    const names = maskLabels(mask, 32).filter((s) => !s.startsWith('Ch '));
    fields.push(field('Valid bits per sample', valid, x, 2, 'How many of the container bits hold real signal, e.g. 20 of 24.'));
    fields.push(field('Channel mask', `${hex(mask, 8)} · ${names.join(', ') || 'none'}${LAYOUTS[mask] ? ` (${LAYOUTS[mask]})` : ''}`, x + 2, 4, 'One bit per speaker position. The channels in each frame follow the order of the set bits.', mask));
    const d1 = r.u32(b, x + 6);
    const g = guid(b, x + 6, r);
    const std = g.endsWith('-0000-0010-8000-00AA00389B71');
    const amb = g.endsWith('-0721-11D3-8644-C8C1CA000000');
    if (std || amb) f.subTag = d1;
    fields.push(field('Sub-format GUID', `${g} · ${std ? tagName(d1) : amb ? `Ambisonic B-format, ${tagName(d1)}` : 'unknown'}`, x + 6, 16, 'The real coding. Its first 4 bytes hold the classic format tag.'));
    if (f.subTag !== undefined) f.eff = f.subTag;
  } else if ((tag === 0x11 || tag === 0x02) && xEnd - x >= 2) {
    f.samplesPerBlock = r.u16(b, x);
    fields.push(field('Samples per block', f.samplesPerBlock, x, 2, 'Each ADPCM block decodes to this many sample frames.'));
    if (tag === 0x02 && xEnd - x >= 4) {
      const n = r.u16(b, x + 2);
      fields.push(field('Coefficient pairs', n, x + 2, 2, 'The predictor coefficient sets the encoder can choose from.'));
      if (xEnd - x > 4) fields.push(field('Coefficients', `${Math.min(n, (xEnd - x - 4) >> 2)} pairs`, x + 4, xEnd - x - 4, 'Each pair predicts the next sample from the two before it.'));
    }
  } else if (tag === 0x55 && xEnd - x >= 12) {
    fields.push(field('MPEG layer-3 ID', r.u16(b, x), x, 2, 'Always 1 for MP3.'));
    fields.push(field('Flags', hex(r.u32(b, x + 2), 8), x + 2, 4, 'Padding flags. 2 means the frames have no padding bytes.'));
    fields.push(field('Block size', `${r.u16(b, x + 6)} bytes`, x + 6, 2, 'The size of one MP3 frame, or an average for a variable bitrate.'));
    fields.push(field('Frames per block', r.u16(b, x + 8), x + 8, 2, 'MP3 frames per block. Usually 1.'));
    fields.push(field('Codec delay', `${r.u16(b, x + 10)} samples`, x + 10, 2, 'Silent samples the decoder adds at the start.'));
  } else if (xEnd > x) {
    fields.push(field('Extra format bytes', fmtBytes(xEnd - x), x, xEnd - x, 'Settings that only this codec understands.'));
  }
  return f;
}

function guid(b: Uint8Array, o: number, r: Reader): string {
  const h = (v: number, w: number) => v.toString(16).toUpperCase().padStart(w, '0');
  const tail = Array.from(b.subarray(o + 8, o + 16), (x) => h(x, 2)).join('');
  return `${h(r.u32(b, o), 8)}-${h(r.u16(b, o + 4), 4)}-${h(r.u16(b, o + 6), 4)}-${tail.slice(0, 4)}-${tail.slice(4)}`;
}

/** Sample layout for codings we can show as numbers; undefined for compressed data. */
function sampleFormatOf(f: Fmt, be: boolean): SampleFormat | undefined {
  if (!f.channels || !f.blockAlign) return undefined;
  const bytes = f.blockAlign % f.channels === 0 ? f.blockAlign / f.channels : Math.ceil(f.bits / 8);
  const labels = f.channelMask ? maskLabels(f.channelMask, f.channels) : defaultLabels(f.channels);
  const base = { bytes, bigEndian: be, channels: f.channels, blockAlign: f.blockAlign, labels };
  if (f.eff === 1 && bytes >= 1 && bytes <= 4) return { ...base, coding: bytes === 1 ? 'uint8' : 'int' };
  if (f.eff === 3 && (bytes === 4 || bytes === 8)) return { ...base, coding: 'float' };
  if ((f.eff === 6 || f.eff === 7) && bytes === 1) return { ...base, coding: f.eff === 6 ? 'alaw' : 'ulaw' };
  return undefined;
}

function headerFields(ctx: Ctx, c: RawChunk, extra: Field[] = []): Field[] {
  let sizeText = `${c.declared.toLocaleString('en-US')} bytes`;
  if (c.sizeSource === 'ds64') sizeText = `${hex(c.declared, 8)} · real size ${(c.bodyEnd - c.bodyStart).toLocaleString('en-US')} bytes is in ds64`;
  else if (c.sizeSource) sizeText += ` · ${(c.bodyEnd - c.bodyStart).toLocaleString('en-US')} bytes actually present`;
  return fitFields([
    field('Chunk ID', `"${c.id}"`, c.start, 4, 'Four ASCII letters that name the chunk. Programs skip IDs they do not know.'),
    field('Chunk size', sizeText, c.start + 4, 4, `Length of the chunk body, not counting these 8 header bytes${ctx.r.be ? ' (big-endian in RIFX)' : ''}.`, c.declared),
    ...extra,
  ], c.start, c.end);
}

function headerSeg(ctx: Ctx, c: RawChunk, len = 8, extra: Field[] = []): Segment {
  const end = Math.min(c.bodyEnd, c.start + len);
  return { kind: 'header', type: 'wav.chunk.header', name: 'Chunk header', start: c.start, end, fields: fitFields(headerFields(ctx, c, extra), c.start, end) };
}

/** Builds a chunk segment: header child, optional body children, and the pad byte. */
function chunkSeg(
  ctx: Ctx, c: RawChunk, kind: Segment['kind'], type: string, body: { fields?: Field[]; children?: Segment[]; summary?: string; name?: string; headerLen?: number; headerExtra?: Field[] },
): Segment {
  const children: Segment[] = [headerSeg(ctx, c, body.headerLen, body.headerExtra), ...(body.children ?? [])];
  if (c.pad) children.push(padSegment(ctx.b, c.bodyEnd, 'wav.pad'));
  return {
    kind, type, name: body.name ?? `${c.id.trim() || c.id} chunk`, start: c.start, end: c.end, summary: body.summary,
    fields: fitFields([...headerFields(ctx, c, body.headerExtra), ...(body.fields ?? [])], c.start, c.end),
    children, warning: c.warning,
  };
}

function strField(b: Uint8Array, name: string, o: number, len: number, note: string): [Field, string] {
  const s = chunkText(b, o, len);
  return [field(name, quote(s), o, len, note), s];
}

function buildList(ctx: Ctx, c: RawChunk): Segment {
  const { b, r } = ctx;
  const listType = ascii(b, c.bodyStart, 4);
  const typeField = field('List type', `"${listType}"`, c.bodyStart, 4, 'Says what kind of items the list holds. INFO holds text tags.');
  const items: Segment[] = [];
  let p = c.bodyStart + 4;
  while (p + 8 <= c.bodyEnd && validId(b, p)) {
    const id = ascii(b, p, 4);
    const len = Math.min(r.u32(b, p + 4), c.bodyEnd - p - 8);
    const s = p + 8, e = s + len;
    const pad = len % 2 === 1 && e < c.bodyEnd;
    const hdr = [field('ID', `"${id}"`, p, 4, 'Names this item.'), field('Size', `${len} bytes`, p + 4, 4, 'Length of the text or data that follows.', len)];
    const seg: Segment = { kind: 'metadata', type: 'wav.list.item', name: id, start: p, end: e + (pad ? 1 : 0), fields: hdr };
    if (listType === 'INFO') {
      const text = chunkText(b, s, len);
      const key = INFO_KEYS[id] ?? id;
      seg.type = 'wav.info.item';
      seg.name = `${id} · ${key}`;
      seg.summary = quote(text, 80);
      hdr.push(field(key, quote(text, 500), s, len, `Text of the ${id} item, ended by a zero byte.`));
      if (text) ctx.tags.push({ key, value: text, rawKey: id });
      if (id === 'ISFT' && text) ctx.encoder = text;
    } else if (listType === 'adtl' && (id === 'labl' || id === 'note') && len >= 4) {
      const cue = r.u32(b, s);
      const text = chunkText(b, s + 4, len - 4);
      seg.type = `wav.adtl.${id}`;
      seg.name = `${id} · cue ${cue}`;
      seg.summary = quote(text, 80);
      hdr.push(field('Cue point ID', cue, s, 4, 'The cue point this text belongs to.'));
      hdr.push(field(id === 'labl' ? 'Label' : 'Note', quote(text, 500), s + 4, len - 4, 'Text for the cue point.'));
      if (id === 'labl' && text) ctx.labels.set(cue, text);
    } else if (listType === 'adtl' && id === 'ltxt' && len >= 20) {
      const cue = r.u32(b, s), n = r.u32(b, s + 4);
      seg.type = 'wav.adtl.ltxt';
      seg.name = `ltxt · cue ${cue}`;
      hdr.push(field('Cue point ID', cue, s, 4, 'The cue point this region starts at.'));
      hdr.push(field('Sample length', `${n.toLocaleString('en-US')} samples`, s + 4, 4, 'Length of the region in sample frames.', n));
      hdr.push(field('Purpose', `"${ascii(b, s + 8, 4)}"`, s + 8, 4, 'What the region is for, e.g. "scrp" for script text.'));
      hdr.push(field('Country / language / dialect / code page', `${r.u16(b, s + 12)} / ${r.u16(b, s + 14)} / ${r.u16(b, s + 16)} / ${r.u16(b, s + 18)}`, s + 12, 8, 'Language settings for the text.'));
      if (len > 20) hdr.push(field('Text', quote(chunkText(b, s + 20, len - 20)), s + 20, len - 20, 'Text for the region.'));
    } else if (len > 0) {
      hdr.push(field('Data', fmtBytes(len), s, len, 'Contents of this list item.'));
    }
    if (pad) seg.children = [padSegment(b, e, 'wav.pad')];
    seg.fields = fitFields(hdr, seg.start, seg.end);
    items.push(seg);
    p = e + (pad ? 1 : 0);
  }
  const what = listType === 'INFO' ? 'text tags' : listType === 'adtl' ? 'cue labels and notes' : 'items';
  return chunkSeg(ctx, c, 'metadata', 'wav.chunk.LIST', {
    name: `LIST chunk (${listType})`, headerLen: 12, headerExtra: [typeField], children: items,
    summary: `${plural(items.length, 'item')} · ${what}`,
  });
}

function buildBext(ctx: Ctx, c: RawChunk): Segment {
  const { b } = ctx;
  const p = c.bodyStart, len = c.bodyEnd - p;
  const fields: Field[] = [];
  const [f1, desc] = strField(b, 'Description', p, 256, 'Free text about the recording, up to 256 characters.');
  const [f2, orig] = strField(b, 'Originator', p + 256, 32, 'The device or person that made the recording.');
  const [f3] = strField(b, 'Originator reference', p + 288, 32, 'A unique ID the originator gave the recording.');
  const [f4, date] = strField(b, 'Origination date', p + 320, 10, 'Date of recording, as yyyy-mm-dd.');
  const [f5, time] = strField(b, 'Origination time', p + 330, 8, 'Time of day the recording started, as hh:mm:ss.');
  fields.push(f1, f2, f3, f4, f5);
  const tref = u64le(b, p + 338);
  const sr = ctx.fmt?.sampleRate ?? 0;
  fields.push(field('Time reference', `${tref.toLocaleString('en-US')} samples${sr ? ` · ${fmtClock(Math.round((tref / sr) * 1000) / 1000)} after midnight` : ''}`, p + 338, 8,
    'The start of the recording, counted in samples since midnight. Editors use it to line up takes from many recorders.', tref));
  const ver = u16le(b, p + 346);
  fields.push(field('Version', ver, p + 346, 2, 'Version 1 added the UMID. Version 2 added the loudness values.'));
  const umid = b.subarray(p + 348, p + 412);
  const umidSet = umid.some((x) => x !== 0);
  fields.push(field('UMID', umidSet ? Array.from(umid.subarray(0, 32), (x) => x.toString(16).padStart(2, '0')).join('') + '…' : '(not set)', p + 348, 64, 'A SMPTE Unique Material Identifier for this recording.'));
  const lo = (name: string, o: number, unit: string, note: string) => {
    const v = (u16le(b, o) << 16) >> 16;
    fields.push(field(name, ver >= 2 ? `${frac(v / 100, 2)} ${unit}` : '(not used before version 2)', o, 2, note, v));
  };
  lo('Integrated loudness', p + 412, 'LUFS', 'Average loudness of the whole recording (EBU R 128).');
  lo('Loudness range', p + 414, 'LU', 'How much the loudness varies over the recording.');
  lo('Max true peak', p + 416, 'dBTP', 'The highest peak level, including peaks between samples.');
  lo('Max momentary loudness', p + 418, 'LUFS', 'The loudest 400 ms window.');
  lo('Max short-term loudness', p + 420, 'LUFS', 'The loudest 3-second window.');
  fields.push(field('Reserved', '180 bytes', p + 422, 180, 'Room for future versions. It must be zero.'));
  if (len > 602) {
    const hist = chunkText(b, p + 602, len - 602);
    fields.push(field('Coding history', quote(hist.replace(/\r?\n/g, ' ⏎ '), 400), p + 602, len - 602, 'One line per step the audio went through: analog, A/D converter, edits, encoders.'));
  }
  if (desc) ctx.tags.push({ key: 'Description', value: desc, rawKey: 'bext Description' });
  if (orig) ctx.tags.push({ key: 'Originator', value: orig, rawKey: 'bext Originator' });
  return chunkSeg(ctx, c, 'metadata', 'wav.chunk.bext', {
    name: 'bext chunk (Broadcast Wave)', fields, summary: [orig, date && `${date} ${time}`.trim()].filter(Boolean).join(' · ') || 'Broadcast Wave metadata',
  });
}

function buildCart(ctx: Ctx, c: RawChunk): Segment {
  const { b } = ctx;
  const p = c.bodyStart, len = c.bodyEnd - p;
  const fields: Field[] = [];
  const add = (name: string, o: number, n: number, note: string) => {
    const [f, s] = strField(b, name, p + o, n, note);
    fields.push(f);
    return s;
  };
  add('Version', 0, 4, 'Version of the cart chunk, e.g. "0101".');
  const title = add('Title', 4, 64, 'Title of the cut.');
  const artist = add('Artist', 68, 64, 'Artist of the cut.');
  add('Cut ID', 132, 64, 'The ID a broadcast automation system uses for this cut.');
  add('Client ID', 196, 64, 'The client or advertiser.');
  add('Category', 260, 64, 'A category such as "SPOT" or "MUSIC".');
  add('Classification', 324, 64, 'Extra classification for the cut.');
  add('Out cue', 388, 64, 'Words that tell the announcer the cut is ending.');
  add('Start date', 452, 10, 'First date the cut may air.');
  add('Start time', 462, 8, 'First time of day the cut may air.');
  add('End date', 470, 10, 'Last date the cut may air.');
  add('End time', 480, 8, 'Last time of day the cut may air.');
  add('Producer app', 488, 64, 'The program that made the chunk.');
  add('Producer app version', 552, 64, 'Version of that program.');
  add('User defined', 616, 64, 'Free text for local use.');
  const lvl = (u32le(b, p + 680) | 0);
  fields.push(field('Level reference', signedNum(lvl), p + 680, 4, 'The sample value that means 0 dB for this station.', lvl));
  const timers: string[] = [];
  for (let i = 0; i < 8; i++) {
    const o = p + 684 + i * 8;
    const id = ascii(b, o, 4).replace(/\0/g, '');
    if (id) timers.push(`${id}=${u32le(b, o + 4).toLocaleString('en-US')}`);
  }
  fields.push(field('Post timers', timers.join(', ') || '(none)', p + 684, 64, 'Marker points in samples, such as the end of the intro.'));
  fields.push(field('Reserved', '276 bytes', p + 748, 276, 'Room for future use.'));
  add('URL', 1024, 1024, 'A web link for more about the cut.');
  if (len > 2048) fields.push(field('Tag text', quote(chunkText(b, p + 2048, len - 2048), 300), p + 2048, len - 2048, 'Free text, often XML.'));
  if (title) ctx.tags.push({ key: 'Title', value: title, rawKey: 'cart Title' });
  if (artist) ctx.tags.push({ key: 'Artist', value: artist, rawKey: 'cart Artist' });
  return chunkSeg(ctx, c, 'metadata', 'wav.chunk.cart', { name: 'cart chunk (radio cart)', fields, summary: [title, artist].filter(Boolean).join(' · ') || undefined });
}

function buildCue(ctx: Ctx, c: RawChunk): Segment {
  const { b, r } = ctx;
  const p = c.bodyStart;
  const n = r.u32(b, p);
  const count = Math.min(n, Math.floor((c.bodyEnd - p - 4) / 24));
  const sr = ctx.fmt?.sampleRate ?? 0;
  // Lazy, so labels from a LIST/adtl chunk later in the file are known.
  const points = (): Segment[] => {
  const out: Segment[] = [];
  for (let i = 0; i < count; i++) {
    const o = p + 4 + i * 24;
    const id = r.u32(b, o), pos = r.u32(b, o + 4), off = r.u32(b, o + 20);
    const at = sr ? ` · at ${fmtClock(Math.round((off / sr) * 1000) / 1000)}` : '';
    const l = ctx.labels.get(id);
    out.push({
      kind: 'index', type: 'wav.cue.point', name: `Cue point ${id}`, start: o, end: o + 24,
      summary: `sample ${off.toLocaleString('en-US')}${at}${l ? ` · "${l}"` : ''}`,
      fields: [
        field('Cue point ID', id, o, 4, 'A number that labels and loops use to refer to this point.'),
        field('Position', pos.toLocaleString('en-US'), o + 4, 4, 'Sample position in play order. Only matters when a playlist is present.', pos),
        field('Data chunk ID', `"${ascii(b, o + 8, 4)}"`, o + 8, 4, 'The chunk that holds the point, almost always "data".'),
        field('Chunk start', r.u32(b, o + 12), o + 12, 4, 'Used only by the rare "wavl" list form. Usually 0.'),
        field('Block start', r.u32(b, o + 16), o + 16, 4, 'Byte offset of the block that holds the point, for compressed data. Usually 0.'),
        field('Sample offset', `${off.toLocaleString('en-US')}${at ? at.replace(' · at', ' ·') : ''}`, o + 20, 4, 'Where the point is, in sample frames from the start of the audio.', off),
      ],
    });
  }
  return out;
  };
  const seg = chunkSeg(ctx, c, 'index', 'wav.chunk.cue', {
    name: 'cue chunk', fields: [field('Cue point count', n, p, 4, 'How many 24-byte cue points follow.')],
    summary: plural(count, 'cue point'),
  });
  const fixed = seg.children as Segment[];
  seg.children = () => [fixed[0], ...points(), ...fixed.slice(1)];
  return seg;
}

function buildSmpl(ctx: Ctx, c: RawChunk): Segment {
  const { b, r } = ctx;
  const p = c.bodyStart;
  const note = r.u32(b, p + 12);
  const period = r.u32(b, p + 8);
  const n = r.u32(b, p + 28);
  const fields = [
    field('Manufacturer', hex(r.u32(b, p), 8), p, 4, 'MIDI manufacturer code of the target sampler. 0 means any.'),
    field('Product', hex(r.u32(b, p + 4), 8), p + 4, 4, 'Model of the target sampler. 0 means any.'),
    field('Sample period', `${period.toLocaleString('en-US')} ns${period ? ` (${fmtHz(Math.round(1e9 / period))})` : ''}`, p + 8, 4, 'Time between samples in nanoseconds.', period),
    field('MIDI unity note', `${note} (${midiName(note)})`, p + 12, 4, 'The note at which the sample plays at its original pitch.', note),
    field('MIDI pitch fraction', r.u32(b, p + 16), p + 16, 4, 'Fine tuning above the unity note, as a fraction of a semitone.'),
    field('SMPTE format', r.u32(b, p + 20), p + 20, 4, 'Frame rate for the SMPTE offset: 0 none, 24, 25, 29 or 30.'),
    field('SMPTE offset', hex(r.u32(b, p + 24), 8), p + 24, 4, 'Time code where the sample starts, as hh:mm:ss:ff in 4 bytes.'),
    field('Loop count', n, p + 28, 4, 'How many 24-byte loop records follow.'),
    field('Sampler data size', r.u32(b, p + 32), p + 32, 4, 'Bytes of vendor data after the loops.'),
  ];
  const loops: Segment[] = [];
  const LOOP_TYPES = ['Forward', 'Ping-pong', 'Backward'];
  for (let i = 0; i < n && p + 36 + (i + 1) * 24 <= c.bodyEnd; i++) {
    const o = p + 36 + i * 24;
    const type = r.u32(b, o + 4), s = r.u32(b, o + 8), e = r.u32(b, o + 12), plays = r.u32(b, o + 20);
    loops.push({
      kind: 'index', type: 'wav.smpl.loop', name: `Loop ${i + 1}`, start: o, end: o + 24,
      summary: `${LOOP_TYPES[type] ?? 'Custom'} · samples ${s.toLocaleString('en-US')}–${e.toLocaleString('en-US')}`,
      fields: [
        field('Cue point ID', r.u32(b, o), o, 4, 'Links the loop to a cue point, which can hold its name.'),
        field('Type', `${LOOP_TYPES[type] ?? 'Custom'} (${type})`, o + 4, 4, 'How the loop plays: forward, back and forth, or backward.', type),
        field('Start', s, o + 8, 4, 'First sample frame of the loop.'),
        field('End', e, o + 12, 4, 'Last sample frame of the loop (it is played too).'),
        field('Fraction', r.u32(b, o + 16), o + 16, 4, 'Fine position of the loop end, as a fraction of a sample.'),
        field('Play count', plays === 0 ? 'Forever' : plays, o + 20, 4, 'How many times the loop repeats. 0 means forever.', plays),
      ],
    });
  }
  return chunkSeg(ctx, c, 'index', 'wav.chunk.smpl', { name: 'smpl chunk (sampler)', fields, children: loops, summary: `Root ${midiName(note)} · ${plural(loops.length, 'loop')}` });
}

const NOTES = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'];
export const midiName = (n: number) => (n >= 0 && n < 128 ? `${NOTES[n % 12]}${Math.floor(n / 12) - 1}` : '?');

function buildInst(ctx: Ctx, c: RawChunk): Segment {
  const { b } = ctx;
  const p = c.bodyStart;
  const s8 = (o: number) => (b[o] << 24) >> 24;
  const fields = [
    field('Unshifted note', `${b[p]} (${midiName(b[p])})`, p, 1, 'The MIDI note the sample was recorded at.', b[p]),
    field('Fine tune', `${signedNum(s8(p + 1))} cents`, p + 1, 1, 'Pitch correction in hundredths of a semitone.', s8(p + 1)),
    field('Gain', `${signedNum(s8(p + 2))} dB`, p + 2, 1, 'Volume change to apply on playback.', s8(p + 2)),
    field('Note range', `${midiName(b[p + 3])}–${midiName(b[p + 4])}`, p + 3, 2, 'The keys on which a sampler should use this sample.'),
    field('Velocity range', `${b[p + 5]}–${b[p + 6]}`, p + 5, 2, 'How hard a key must be struck (1–127) to use this sample.'),
  ];
  return chunkSeg(ctx, c, 'metadata', 'wav.chunk.inst', { name: 'inst chunk (instrument)', fields, summary: `Root ${midiName(b[p])}` });
}

function buildAcid(ctx: Ctx, c: RawChunk): Segment {
  const { b } = ctx;
  const p = c.bodyStart;
  const dv = new DataView(b.buffer, b.byteOffset);
  const flags = u32le(b, p);
  const tempo = p + 24 <= b.length ? dv.getFloat32(p + 20, true) : 0;
  const flagNames = [['One-shot', 1], ['Root note set', 2], ['Stretch', 4], ['Disk-based', 8], ['High octave', 16]]
    .filter(([, m]) => flags & (m as number)).map(([n]) => n);
  const fields = [
    field('Flags', `${hex(flags, 8)} · ${flagNames.join(', ') || 'none'}`, p, 4, 'Loop or one-shot, and whether ACID may time-stretch it.', flags),
    field('Root note', `${u16le(b, p + 4)} (${midiName(u16le(b, p + 4))})`, p + 4, 2, 'The key of the loop, used for transposing.'),
    field('Reserved', '6 bytes', p + 6, 6, 'Unused.'),
    field('Beats', u32le(b, p + 12), p + 12, 4, 'Length of the loop in beats.'),
    field('Meter', `${u16le(b, p + 18)}/${u16le(b, p + 16)}`, p + 16, 4, 'Time signature: beats per bar over note value.'),
    field('Tempo', `${+tempo.toFixed(3)} BPM`, p + 20, 4, 'The original tempo of the loop.'),
  ];
  return chunkSeg(ctx, c, 'metadata', 'wav.chunk.acid', { name: 'acid chunk (loop info)', fields, summary: `${+tempo.toFixed(2)} BPM · ${u32le(b, p + 12)} beats` });
}

function buildXml(ctx: Ctx, c: RawChunk): Segment {
  const { b } = ctx;
  const len = c.bodyEnd - c.bodyStart;
  const text = chunkText(b, c.bodyStart, Math.min(len, 64 * 1024));
  const pick = (tag: string) => text.match(new RegExp(`<${tag}>([^<]{1,80})</${tag}>`))?.[1]?.trim();
  const bits = [['Project', pick('PROJECT')], ['Scene', pick('SCENE')], ['Take', pick('TAKE')], ['Tape', pick('TAPE')]].filter(([, v]) => v);
  const shown = text.replace(/\s+/g, ' ').trim();
  return chunkSeg(ctx, c, 'metadata', c.id === 'iXML' ? 'wav.chunk.iXML' : 'wav.chunk.axml', {
    fields: [field('XML', shown.length > 600 ? shown.slice(0, 600) + '…' : shown || '(empty)', c.bodyStart, len, 'The XML text, shown here without line breaks.')],
    summary: bits.map(([k, v]) => `${k} ${v}`).join(' · ') || `${fmtBytes(len)} of XML`,
  });
}

function buildDs64(ctx: Ctx, c: RawChunk): Segment {
  const { b } = ctx;
  const p = c.bodyStart;
  const n = u32le(b, p + 24);
  const fields = [
    field('RIFF size', `${u64le(b, p).toLocaleString('en-US')} bytes`, p, 8, 'The real 64-bit size of the whole file, minus 8.', u64le(b, p)),
    field('data size', `${u64le(b, p + 8).toLocaleString('en-US')} bytes`, p + 8, 8, 'The real 64-bit size of the data chunk.', u64le(b, p + 8)),
    field('Sample count', u64le(b, p + 16).toLocaleString('en-US'), p + 16, 8, 'Sample frames in the file, like the fact chunk.', u64le(b, p + 16)),
    field('Table length', n, p + 24, 4, 'How many other chunks also need a 64-bit size. Usually 0.'),
  ];
  for (let i = 0; i < n && p + 28 + (i + 1) * 12 <= c.bodyEnd && i < 64; i++) {
    const o = p + 28 + i * 12;
    fields.push(field(`Size of "${ascii(b, o, 4)}"`, `${u64le(b, o + 4).toLocaleString('en-US')} bytes`, o, 12, '64-bit size for a chunk whose own size field is 0xFFFFFFFF.'));
  }
  return chunkSeg(ctx, c, 'header', 'wav.chunk.ds64', { name: 'ds64 chunk (64-bit sizes)', fields, summary: `data: ${fmtBytes(u64le(b, p + 8))}` });
}

function buildId3(ctx: Ctx, c: RawChunk): Segment {
  const res = parseId3v2(ctx.b, c.bodyStart);
  const children: Segment[] = [];
  if (res && res.segment.end <= c.bodyEnd) {
    children.push(res.segment);
    ctx.tags.push(...res.tags);
    ctx.pictures.push(...res.pictures);
    ctx.notes.push(...res.notes);
  }
  return chunkSeg(ctx, c, 'metadata', 'wav.chunk.id3', {
    name: `${c.id.trim()} chunk (ID3 tag)`, children,
    fields: children.length ? [] : fitFields([field('Data', fmtBytes(c.bodyEnd - c.bodyStart), c.bodyStart, c.bodyEnd - c.bodyStart, 'This does not start with a valid ID3v2 header.')], c.start, c.end),
    summary: res ? res.segment.name : 'No valid ID3 header',
  });
}

function buildGeneric(ctx: Ctx, c: RawChunk, kind: Segment['kind'], type: string, note: string, summary?: string): Segment {
  const len = c.bodyEnd - c.bodyStart;
  return chunkSeg(ctx, c, kind, type, {
    fields: len > 0 ? [field('Body', fmtBytes(len), c.bodyStart, len, note)] : [],
    summary: summary ?? fmtBytes(len),
  });
}

function parseWav(b: Uint8Array): ParsedFile {
  const size = b.length;
  const magic = ascii(b, 0, 4);
  const be = magic === 'RIFX';
  const rf64 = magic === 'RF64' || magic === 'BW64';
  const r = reader(be);
  const ctx: Ctx = { b, r, size, tags: [], pictures: [], notes: [], labels: new Map() };
  const notes = ctx.notes;
  if (rf64) ctx.ds64 = parseDs64(ctx, 12);

  // Where the RIFF chunk says it ends. Streamed files often leave 0 or 0xFFFFFFFF here.
  const riffSize = r.u32(b, 4);
  let riffEnd = rf64 && ctx.ds64 ? ctx.ds64.riffSize + 8 : riffSize + 8;
  const riffBogus = (!rf64 && (riffSize === 0 || riffSize === 0xffffffff)) || (rf64 && !ctx.ds64);
  if (riffBogus || riffEnd > size) riffEnd = size;

  const container = magic === 'RIFX' ? 'WAV (RIFX, big-endian)' : magic === 'RF64' ? 'WAV (RF64)' : magic === 'BW64' ? 'WAV (BW64)' : 'WAV (RIFF)';
  const hdrEnd = Math.min(12, size);
  const riffSizeText = rf64 ? `${hex(riffSize, 8)} · real size in ds64` : `${riffSize.toLocaleString('en-US')} bytes`;
  const header: Segment = {
    kind: 'header', type: 'wav.riff', name: `${magic} header`, start: 0, end: hdrEnd,
    summary: `${magic} · WAVE`,
    fields: fitFields([
      field('Chunk ID', `"${magic}"`, 0, 4, be ? 'RIFX is RIFF with big-endian numbers.' : rf64 ? `${magic} is RIFF with 64-bit sizes, for files over 4 GB.` : 'Marks a RIFF file. Its numbers are little-endian.'),
      field('RIFF size', riffSizeText, 4, 4, 'Size of everything after these 8 bytes, so the file size minus 8.', riffSize),
      field('Form type', `"${ascii(b, 8, 4)}"`, 8, 4, 'Says what the RIFF file holds. WAVE means audio.'),
    ], 0, hdrEnd),
  };
  if (!riffBogus && !rf64 && riffSize + 8 !== size) {
    header.warning = riffSize + 8 > size
      ? `The header says the file is ${(riffSize + 8).toLocaleString('en-US')} bytes, but it is only ${size.toLocaleString('en-US')}. The file is probably truncated.`
      : `The header says the file is ${(riffSize + 8).toLocaleString('en-US')} bytes, but it is ${size.toLocaleString('en-US')}. There are extra bytes after the RIFF chunk.`;
  } else if (riffBogus) {
    header.warning = 'The RIFF size was never filled in. This is common for streamed or interrupted recordings.';
  }

  const { chunks, tail } = walkChunks(ctx, riffEnd, rf64);
  const fmtChunk = chunks.find((c) => c.id === 'fmt ');
  if (fmtChunk) ctx.fmt = parseFmt(ctx, fmtChunk);
  const fact = chunks.find((c) => c.id === 'fact');
  if (fact && fact.bodyEnd - fact.bodyStart >= 4) ctx.factSamples = r.u32(b, fact.bodyStart);
  const fmt = ctx.fmt;
  const sf = fmt ? sampleFormatOf(fmt, be) : undefined;

  const segments: Segment[] = [header];
  const frames: Segment[] = [];
  let audio: AudioInfo = { codec: 'Unknown', lossless: false };
  let pcm: PcmLayout | undefined;
  let dataBytes = 0;
  let firstData = true;

  for (const c of chunks) {
    const id = c.id;
    let seg: Segment;
    if (id === 'fmt ' && fmt) {
      seg = chunkSeg(ctx, c, 'header', 'wav.chunk.fmt', {
        name: 'fmt chunk', fields: fmt.fields,
        summary: `${tagName(fmt.eff)} · ${fmtHz(fmt.sampleRate)} · ${channelName(fmt.channels)}${fmt.bits ? ` · ${fmt.bits}-bit` : ''}`,
      });
    } else if (id === 'data') {
      const len = c.bodyEnd - c.bodyStart;
      seg = chunkSeg(ctx, c, 'audio', 'wav.chunk.data', { name: 'data chunk' });
      if (firstData) {
        firstData = false;
        dataBytes = len;
        const res = buildDataChunk(ctx, c, seg, sf);
        audio = res.audio;
        pcm = res.pcm;
        if (seg.time) frames.push(seg);
      } else {
        seg.warning = 'A second data chunk. Players only play the first one.';
      }
    } else if (id === 'fact') {
      const n = ctx.factSamples ?? 0;
      const sr = fmt?.sampleRate;
      seg = chunkSeg(ctx, c, 'header', 'wav.chunk.fact', {
        name: 'fact chunk',
        fields: [field('Sample frames', `${n.toLocaleString('en-US')}${sr ? ` (${fmtClock(Math.round((n / sr) * 1000) / 1000)})` : ''}`, c.bodyStart, 4, 'Length of the audio in sample frames. Compressed formats need it, because bytes do not map evenly to time.', n)],
        summary: `${n.toLocaleString('en-US')} sample frames`,
      });
    } else if (id === 'LIST') seg = buildList(ctx, c);
    else if (id === 'bext') seg = buildBext(ctx, c);
    else if (id === 'cart') seg = buildCart(ctx, c);
    else if (id === 'cue ') seg = buildCue(ctx, c);
    else if (id === 'smpl') seg = buildSmpl(ctx, c);
    else if (id === 'inst') seg = buildInst(ctx, c);
    else if (id === 'acid') seg = buildAcid(ctx, c);
    else if (id === 'ds64') seg = buildDs64(ctx, c);
    else if (XML_IDS.has(id)) seg = buildXml(ctx, c);
    else if (id === 'id3 ' || id === 'ID3 ' || id === 'ID32') seg = buildId3(ctx, c);
    else if (PADDING_IDS.has(id)) {
      seg = buildGeneric(ctx, c, 'padding', 'wav.chunk.padding', 'Empty space. Players skip it.', `${fmtBytes(c.bodyEnd - c.bodyStart)} reserved`);
      seg.name = `${id.trim() || 'blank'} chunk (padding)`;
    } else if (KNOWN_OPAQUE[id]) seg = buildGeneric(ctx, c, KNOWN_OPAQUE[id][0], KNOWN_OPAQUE[id][1], 'The chunk contents. This tool does not decode them field by field.');
    else seg = buildGeneric(ctx, c, 'unknown', 'wav.chunk.unknown', `Contents of an unknown "${id}" chunk. Players skip it by its size.`, `Unknown chunk "${id}" · ${fmtBytes(c.bodyEnd - c.bodyStart)}`);
    segments.push(seg);
  }

  // Bytes after the last chunk: an appended ID3 tag, or leftovers.
  let pos = tail;
  if (pos < size) {
    const id3 = parseId3v2(b, pos);
    if (id3 && id3.end > pos && id3.segment.start === pos && id3.segment.end <= size) {
      segments.push(id3.segment);
      ctx.tags.push(...id3.tags);
      ctx.pictures.push(...id3.pictures);
      notes.push({ level: 'info', text: 'An ID3v2 tag is stuck on after the RIFF data. Some players read it; the WAV format itself ignores it.' }, ...id3.notes);
      pos = id3.segment.end;
    }
  }
  if (pos < size && size - pos === 128) {
    const v1 = parseId3v1(b, pos);
    if (v1) {
      segments.push(v1.segment);
      ctx.tags.push(...v1.tags);
      pos = v1.end;
    }
  }
  if (pos < size) {
    segments.push({
      kind: 'unknown', type: 'wav.trailing', name: 'Trailing bytes', start: pos, end: size, summary: fmtBytes(size - pos),
      warning: 'These bytes are not a valid chunk. They may be damage, or data another program added.',
    });
  }

  // ── Notes that teach ──
  if (!fmt) notes.push({ level: 'warn', text: 'There is no fmt chunk, so nothing says how to read the audio.' });
  if (!chunks.some((c) => c.id === 'data')) notes.push({ level: 'warn', text: 'There is no data chunk, so the file holds no audio.' });
  if (fmt) {
    const rate = fmt.byteRate * 8;
    if (sf && fmt.sampleRate && fmt.bits) {
      notes.push({ level: 'info', text: `The data rate is ${fmtKbps(rate)}: every second stores ${fmt.sampleRate.toLocaleString('en-US')} × ${plural(fmt.channels, 'channel')} × ${sf.bytes * 8} bits.` });
    }
    if (sf && fmt.blockAlign !== fmt.channels * sf.bytes) notes.push({ level: 'warn', text: `Block align is ${fmt.blockAlign}, but ${fmt.channels} channels × ${sf.bytes} bytes gives ${fmt.channels * sf.bytes}.` });
    if (sf && fmt.byteRate !== fmt.sampleRate * fmt.blockAlign) {
      notes.push({ level: 'warn', text: `The byte rate says ${fmt.byteRate.toLocaleString('en-US')}, but sample rate × block align is ${(fmt.sampleRate * fmt.blockAlign).toLocaleString('en-US')}. Players may show the wrong length.` });
    }
    if (fmt.tag === 0xfffe) notes.push({ level: 'info', text: 'This file uses WAVE_FORMAT_EXTENSIBLE. The extra fields name the speaker layout and the valid bits, which the old format could not say.' });
    if (sf?.coding === 'uint8') notes.push({ level: 'info', text: '8-bit WAV samples are unsigned: 128 is silence. Wider samples are signed, with 0 as silence.' });
    if (sf && fmt.validBits && fmt.validBits < sf.bytes * 8) notes.push({ level: 'info', text: `Only ${fmt.validBits} of every ${sf.bytes * 8} bits hold signal. The low bits are padding.` });
    if (!sf && ctx.factSamples === undefined && fmt.eff !== 1 && fmt.eff !== 3) notes.push({ level: 'info', text: 'The audio is compressed but there is no fact chunk, so the length is estimated from the byte rate.' });
    if (fmt.channels > 2 && fmt.tag !== 0xfffe) notes.push({ level: 'warn', text: 'This file has more than two channels but no channel mask, so players must guess the speaker layout.' });
  }
  if (dataBytes > 0) {
    const overhead = size - dataBytes;
    const pct = (dataBytes / size) * 100;
    notes.push({ level: 'info', text: `The audio samples use ${pct > 99.99 ? 'over 99.99' : pct.toFixed(2)}% of the file. Headers and metadata take the other ${overhead.toLocaleString('en-US')} bytes.` });
  }
  const odd = chunks.filter((c) => c.pad);
  if (odd.length) notes.push({ level: 'info', text: `${odd.map((c) => `"${c.id.trim()}"`).join(', ')} ${odd.length === 1 ? 'has' : 'have'} an odd size, so a zero pad byte follows. RIFF chunks always start on an even offset.` });
  if (rf64) {
    notes.push({ level: 'info', text: `This is an ${magic} file. The 32-bit size fields hold 0xFFFFFFFF and the real 64-bit sizes are in the ds64 chunk, so the file can be larger than 4 GB.` });
    if (!ctx.ds64) notes.push({ level: 'warn', text: 'The ds64 chunk is missing, so the real sizes are unknown.' });
  } else if (fmt && fmt.byteRate > 0) {
    const hours = 0xffffffff / fmt.byteRate / 3600;
    notes.push({ level: 'info', text: `RIFF size fields are 32-bit, so a WAV file can hold at most 4 GB. At this data rate that is about ${hours >= 10 ? Math.round(hours) : hours.toFixed(1)} hours. RF64 removes the limit.` });
  }
  for (const c of chunks) {
    if (PADDING_IDS.has(c.id) && c.bodyEnd - c.bodyStart >= 28) {
      notes.push({ level: 'info', text: `A ${c.id.trim()} chunk reserves ${fmtBytes(c.bodyEnd - c.bodyStart)}. Recorders often write one at the start so they can turn it into a ds64 chunk if the file grows past 4 GB.` });
      break;
    }
  }
  const dataIdx = chunks.findIndex((c) => c.id === 'data');
  if (dataIdx >= 0 && chunks.slice(dataIdx + 1).some((c) => c.id === 'LIST' || c.id === 'id3 ')) {
    notes.push({ level: 'info', text: 'Some tags come after the audio data. A player that streams the file only sees them at the end.' });
  }
  if (audio.encoder === undefined && ctx.encoder) audio.encoder = ctx.encoder;

  return { format: 'wav', container, size, segments, frames, audio, tags: ctx.tags, pictures: ctx.pictures, notes, pcm };
}

/** Fills in the data chunk: time, lazy per-second children, lazy sample fields, AudioInfo and PcmLayout. */
function buildDataChunk(ctx: Ctx, c: RawChunk, seg: Segment, sf: SampleFormat | undefined): { audio: AudioInfo; pcm?: PcmLayout } {
  const { b } = ctx;
  const fmt = ctx.fmt;
  const len = c.bodyEnd - c.bodyStart;
  if (!fmt) {
    seg.summary = `${fmtBytes(len)} · format unknown`;
    return { audio: { codec: 'Unknown', lossless: false } };
  }
  const sr = fmt.sampleRate;
  const endian = ctx.r.be ? 'big-endian' : 'little-endian';
  let duration: number | undefined;
  let totalSamples: number | undefined;
  if (sf && sr > 0) {
    totalSamples = Math.floor(len / sf.blockAlign);
    duration = totalSamples / sr;
  } else {
    const n = ctx.ds64?.sampleCount || ctx.factSamples;
    if (n && sr > 0) {
      totalSamples = n;
      duration = n / sr;
    } else if (fmt.byteRate > 0) duration = len / fmt.byteRate;
  }
  if (duration !== undefined && !Number.isFinite(duration)) duration = undefined;

  const [short, long] = FORMAT_TAGS[fmt.eff] ?? [tagName(fmt.eff), `Format tag ${hex(fmt.eff, 4)}`];
  const audio: AudioInfo = {
    codec: short, codecLong: long, sampleRate: sr || undefined, channels: fmt.channels || undefined, duration, totalSamples,
    lossless: sf?.coding === 'int' || sf?.coding === 'uint8' || sf?.coding === 'float',
    channelMode: fmt.channelMask && LAYOUTS[fmt.channelMask] ? LAYOUTS[fmt.channelMask] : fmt.channels ? channelName(fmt.channels) : undefined,
  };
  if (sf) {
    const bits = fmt.validBits || fmt.bits || sf.bytes * 8;
    audio.bitsPerSample = sf.coding === 'alaw' || sf.coding === 'ulaw' ? 8 : bits;
    audio.bitrate = sr * sf.blockAlign * 8;
    audio.bitrateMode = audio.lossless ? 'Uncompressed' : 'CBR';
    if (sf.coding === 'uint8') audio.codecLong = `Linear PCM, 8-bit unsigned integer`;
    else if (sf.coding === 'int') audio.codecLong = `Linear PCM, ${bits}-bit signed integer${bits !== sf.bytes * 8 ? ` in ${sf.bytes * 8}-bit container` : ''}, ${endian}`;
    else if (sf.coding === 'float') audio.codecLong = `Linear PCM, ${sf.bytes * 8}-bit IEEE floating point, ${endian}`;
  } else {
    if (fmt.bits && (fmt.eff === 2 || fmt.eff === 0x11)) audio.bitsPerSample = fmt.bits;
    if (duration && duration > 0) audio.bitrate = Math.round((len * 8) / duration);
    else if (fmt.byteRate) audio.bitrate = fmt.byteRate * 8;
    if (fmt.eff === 2 || fmt.eff === 0x11) audio.bitrateMode = 'CBR';
  }

  if (duration !== undefined) seg.time = [0, duration];
  seg.summary = `${fmtBytes(len)}${duration !== undefined ? ` · ${fmtClock(Math.round(duration * 100) / 100)}` : ''} of ${short}${sf ? '' : ' (compressed)'}`;

  const header = headerSeg(ctx, c);
  const blocks = () =>
    timeBlocks(b, {
      start: c.bodyStart, end: c.bodyEnd, duration: duration ?? 0, type: 'wav.data.block',
      bytesPerSecond: sf ? sr * sf.blockAlign : undefined, align: sf ? sf.blockAlign : fmt.blockAlign, sample: sf,
    });
  const pad = c.pad ? [padSegment(b, c.bodyEnd, 'wav.pad')] : [];
  seg.children = () => [header, ...blocks(), ...pad];
  const hdrFields = headerFields(ctx, c);
  seg.fields = () => {
    if (sf) return [...hdrFields, ...sampleFields(b, c.bodyStart, c.bodyEnd, sf, 0, 16)];
    return fitFields([...hdrFields, field(`${short} data`, fmtBytes(len), c.bodyStart, len, 'Compressed audio. This tool does not decode it into sample values.')], c.start, c.end);
  };
  if (sf && len % sf.blockAlign !== 0) {
    ctx.notes.push({ level: 'warn', text: `The data chunk ends with a partial sample frame (${len % sf.blockAlign} extra bytes). The file was probably cut off.` });
  }

  let pcm: PcmLayout | undefined;
  if (sf && (sf.coding === 'int' || sf.coding === 'uint8' || sf.coding === 'float')) {
    pcm = {
      dataStart: c.bodyStart, dataEnd: c.bodyEnd, channels: sf.channels, bitsPerSample: sf.bytes * 8,
      blockAlign: sf.blockAlign, float: sf.coding === 'float', bigEndian: sf.bigEndian, sampleRate: sr,
    };
  }
  return { audio, pcm };
}

export const wavParser: FormatParser = {
  id: 'wav',
  detect(b) {
    return b.length >= 12 && (matches(b, 0, 'RIFF') || matches(b, 0, 'RIFX') || matches(b, 0, 'RF64') || matches(b, 0, 'BW64')) && matches(b, 8, 'WAVE');
  },
  parse: parseWav,
  explanations: {
    'wav.riff': {
      title: 'RIFF header',
      body: 'The first 12 bytes of every WAV file. `RIFF` says the file is a list of chunks, the size covers the rest of the file, and `WAVE` says the chunks hold audio. RIFF came from Microsoft and IBM in 1991, so its numbers are little-endian, like Intel CPUs.',
    },
    'wav.chunk.header': {
      title: 'Chunk header',
      body: 'Every chunk starts with a four-letter ID and a 4-byte size. With the size, a program can jump over any chunk it does not understand. This is why new chunk types can be added without breaking old players.',
    },
    'wav.pad': {
      title: 'Pad byte',
      body: 'RIFF chunks must start on an even byte offset. When a chunk has an odd size, one zero byte follows it. The chunk size does not count this byte.',
    },
    'wav.chunk.fmt': {
      title: 'fmt chunk (format)',
      body: 'Says how to read the audio: the coding, the number of channels, the sample rate and the bits per sample. **Block align** is the size of one sample frame. Players use it to stay on frame boundaries, so a wrong value turns the sound into noise.',
    },
    'wav.chunk.data': {
      title: 'data chunk (the audio)',
      body: 'The audio itself. For PCM, the samples for each channel are interleaved, one sample frame after another, with no compression. So each second always takes the same number of bytes, and a player can jump to any time with simple arithmetic.',
    },
    'wav.data.block': {
      title: 'Audio samples',
      body: 'A slice of the data chunk. It holds the samples for this span of time. For PCM, each sample is a number that gives the position of the speaker cone at one instant.',
    },
    'wav.chunk.fact': {
      title: 'fact chunk',
      body: 'Holds the length of the audio in sample frames. Compressed formats need it, because their byte count does not map evenly to time. PCM files do not need it, but some programs write it anyway.',
    },
    'wav.chunk.LIST': {
      title: 'LIST chunk',
      body: 'A chunk that holds smaller sub-chunks. A `LIST` of type `INFO` holds text tags such as title (INAM) and artist (IART). A `LIST` of type `adtl` holds labels and notes for cue points.',
    },
    'wav.info.item': {
      title: 'INFO tag',
      body: 'One text tag in a LIST/INFO chunk. The four-letter ID names the tag, for example INAM for the title or ISFT for the software that wrote the file. The text ends with a zero byte.',
    },
    'wav.list.item': {
      title: 'List item',
      body: 'One sub-chunk inside a LIST chunk. It has the same ID-and-size header as a top-level chunk.',
    },
    'wav.adtl.labl': {
      title: 'Cue label',
      body: 'A name for a cue point, like "Chorus" or "Take 3". The cue point ID links it to a point in the cue chunk.',
    },
    'wav.adtl.note': {
      title: 'Cue note',
      body: 'A longer comment for a cue point. The cue point ID links it to a point in the cue chunk.',
    },
    'wav.adtl.ltxt': {
      title: 'Labeled text region',
      body: 'Text for a region of the audio that starts at a cue point and lasts a given number of samples. Speech tools use it for captions or script lines.',
    },
    'wav.chunk.bext': {
      title: 'bext chunk (Broadcast Wave)',
      body: 'Added by the EBU for radio and film work. It records who made the recording, when, and the **time reference**: the start time in samples since midnight. Editors use this to line up audio from several recorders. Version 2 also stores loudness values.',
    },
    'wav.chunk.iXML': {
      title: 'iXML chunk',
      body: 'Production metadata as XML text, written by field recorders. It often names the project, scene, take and track names, so an editor can sort hundreds of files.',
    },
    'wav.chunk.axml': {
      title: 'axml chunk',
      body: 'XML metadata, often in the EBU Core or ADM format. It can describe the programme, or the objects in an immersive (3D) mix.',
    },
    'wav.chunk.id3': {
      title: 'ID3 chunk',
      body: 'An ID3v2 tag, the same kind MP3 files use, inside a RIFF chunk. Music software adds it because ID3 can hold more tags and cover art than LIST/INFO can.',
    },
    'wav.chunk.cue': {
      title: 'cue chunk (markers)',
      body: 'A list of markers in the audio. Each cue point gives a sample position. Editors show them as markers, and samplers use them as loop points. Their names are in a LIST/adtl chunk.',
    },
    'wav.cue.point': {
      title: 'Cue point',
      body: 'One marker: an ID and the sample frame where it sits. Divide the sample offset by the sample rate to get the time in seconds.',
    },
    'wav.chunk.smpl': {
      title: 'smpl chunk (sampler)',
      body: 'Tells a hardware or software sampler how to play this sound as an instrument: the root note and the loops. A loop lets a short recording sustain as long as a key is held.',
    },
    'wav.smpl.loop': {
      title: 'Sample loop',
      body: 'A region the sampler repeats while a key is held. It gives the start and end sample, the direction, and how many times to repeat.',
    },
    'wav.chunk.inst': {
      title: 'inst chunk (instrument)',
      body: 'Tells a sampler which keys and how hard a key press this sample should cover, and its root note, tuning and gain.',
    },
    'wav.chunk.acid': {
      title: 'acid chunk (loop info)',
      body: 'Added by Sony ACID for music loops. It stores the tempo, the number of beats and the key, so software can fit the loop to a song.',
    },
    'wav.chunk.cart': {
      title: 'cart chunk (radio)',
      body: 'The AES46 "cart chunk" for radio automation systems. It stores the title, artist, cut ID, the dates the cut may air, and timing markers such as the end of the intro.',
    },
    'wav.chunk.ds64': {
      title: 'ds64 chunk (64-bit sizes)',
      body: 'RIFF sizes are 32-bit, so a normal WAV file stops at 4 GB. RF64 and BW64 files write 0xFFFFFFFF in the old size fields and keep the real 64-bit sizes here.',
    },
    'wav.chunk.padding': {
      title: 'Padding chunk',
      body: 'Empty space that players skip. Recorders often write a `JUNK` chunk near the start so that, if the recording grows past 4 GB, they can turn it into a ds64 chunk and make the file RF64 without moving the audio. Other programs use it to align the audio to a disk sector.',
    },
    'wav.chunk.chna': {
      title: 'chna chunk (channel allocation)',
      body: 'Links each audio track to an object or channel in the ADM metadata (the axml chunk). Immersive formats like Dolby Atmos masters use it.',
    },
    'wav.chunk.PEAK': {
      title: 'PEAK chunk',
      body: 'Stores the highest sample value of each channel and where it is. Editors read it to draw levels without scanning the whole file.',
    },
    'wav.chunk.levl': {
      title: 'levl chunk (peak envelope)',
      body: 'A pre-computed overview of the waveform (EBU Tech 3285 s3), so an editor can draw it at once without reading all the audio.',
    },
    'wav.chunk.DISP': {
      title: 'DISP chunk',
      body: 'Something for a program to show when it lists the file, usually a text title or a small picture. It is an old Windows feature.',
    },
    'wav.chunk.umid': {
      title: 'umid chunk',
      body: 'A SMPTE Unique Material Identifier: a worldwide unique ID for this recording.',
    },
    'wav.chunk.plst': {
      title: 'plst chunk (playlist)',
      body: 'A play order made of cue points, so a player can play sections of the audio in a different order.',
    },
    'wav.chunk.pro': {
      title: 'Application chunk',
      body: 'Private data from an audio program, such as Pro Tools or Sound Forge regions and settings. Other programs skip it.',
    },
    'wav.chunk.unknown': {
      title: 'Unknown chunk',
      body: 'A chunk this tool does not know. The size in its header lets every player skip it safely. This is how RIFF lets programs add their own data without breaking others.',
    },
    'wav.trailing': {
      title: 'Trailing bytes',
      body: 'Bytes after the last complete chunk. They do not form a valid chunk header, so players ignore them.',
    },
  } satisfies Record<string, Explanation>,
};
